import { useEffect, useMemo, useRef, useState } from "react";
import { Polyline, CircleMarker, Marker } from "react-leaflet";
import { DataPoint } from "./geolocationApi";
import { LatLng, LeafletMouseEvent } from "leaflet";
import { TooltipVehicleTrip } from "./tooltipVehicleTrip";
import { TooltipTripCard } from "./tooltipTripCard";
import { VehicleGeolocated } from "./index";
import {
  dateFormatterDayMonthYear2Digits,
  dateFormatterHourMinute,
  getCarBearing,
  getJunctionDataPoints,
  getArrowPositions,
  getTripDataPoints,
} from "@qivia/ui";
import styled from "styled-components";
import { getClosestSegmentIndex } from "./getClosestSegmentIndex";
import L from "leaflet";
import { Trips, TripsDetails } from "../fleet/trips/tripsApi";
import startTripIconUrl from "../../../assets/startTripIcon.svg";
import endTripIconUrl from "../../../assets/endTripIcon.svg";
import junctionTripIconUrl from "../../../assets/junctionTripIcon.svg";
import carTripIconUrl from "../../../assets/carTripIcon.svg";

const ARROW_DEFAULT_COLOR = "#1A353A";
const TRIP_HIGHLIGHT_COLOR = "#22C55E";

export const VehicleTripOnMap = (props: {
  showTripForVehicleUuid: string | null;
  isNotProductionEnvironment: () => boolean;
  fakeVehicleGeolocatedForDemo: VehicleGeolocated[];
  lastDataPointsVehiclesToDisplayed: VehicleGeolocated[] | null;
  isOngoing: boolean;
  tripsDetailsForSelectedVehicle: TripsDetails[];
  driverByTripUuid: Record<string, Trips["driver"]>;
  onRequestTripAddresses: (tripUuid: string) => void;
  tripUuidWaitingForAddresses: string | null;
}) => {
  const {
    showTripForVehicleUuid,
    isNotProductionEnvironment,
    fakeVehicleGeolocatedForDemo,
    lastDataPointsVehiclesToDisplayed,
    isOngoing,
    tripsDetailsForSelectedVehicle,
    driverByTripUuid,
    onRequestTripAddresses,
    tripUuidWaitingForAddresses,
  } = props;

  const vehicleTripToDisplay = useMemo(() => {
    if (!showTripForVehicleUuid) return null;
    if (isNotProductionEnvironment()) {
      return fakeVehicleGeolocatedForDemo?.find(
        (v) => v.uuid === showTripForVehicleUuid,
      );
    } else {
      return lastDataPointsVehiclesToDisplayed?.find(
        (v) => v.uuid === showTripForVehicleUuid,
      );
    }
  }, [
    fakeVehicleGeolocatedForDemo,
    isNotProductionEnvironment,
    lastDataPointsVehiclesToDisplayed,
    showTripForVehicleUuid,
  ]);

  const polylineRef = useRef<L.Polyline | null>(null);
  const [hoverTripVehicleAndDataPoint, setHoverTripVehicleAndDataPoint] =
    useState<{
      vehicle: VehicleGeolocated;
      dataPoint: DataPoint;
    } | null>(null);
  const [mouseLatLng, setMouseLatLng] = useState<LatLng | null>(null);
  const [selected, setSelected] = useState<{
    trip: TripsDetails;
    latLng: LatLng;
    date: string;
  } | null>(null);

  const dataPoints = useMemo(
    () => vehicleTripToDisplay?.dataPoints ?? [],
    [vehicleTripToDisplay?.dataPoints],
  );

  useEffect(() => {
    setSelected(null);
  }, [showTripForVehicleUuid]);

  useEffect(() => {
    if (!vehicleTripToDisplay) return;
    const polyline = polylineRef.current;
    if (!polyline) return;
    const map = (polyline as unknown as { _map: L.Map })._map;
    if (!map) return;

    const latlngs = polyline.getLatLngs() as LatLng[];

    const onMove = (e: LeafletMouseEvent) => {
      //on set la souris en focntion de sa position sur la polyline
      setMouseLatLng(e.latlng);

      //on trouve le segment le plus proche de la souris
      const mouse = e.containerPoint;
      const segmentIndex = getClosestSegmentIndex(latlngs, mouse, map);

      setHoverTripVehicleAndDataPoint({
        vehicle: vehicleTripToDisplay,
        dataPoint: dataPoints[segmentIndex],
      });
    };

    const onLeave = () => {
      setMouseLatLng(null);
      setHoverTripVehicleAndDataPoint(null);
    };

    const clearSelection = () => setSelected(null);

    const onClick = (e: LeafletMouseEvent) => {
      L.DomEvent.stopPropagation(e);
      const mouse = e.containerPoint;
      const segmentIndex = getClosestSegmentIndex(latlngs, mouse, map);
      const clickedDataPoint = dataPoints[segmentIndex];
      const trip =
        tripsDetailsForSelectedVehicle.find(
          (t) =>
            clickedDataPoint.date >= t.startDate &&
            clickedDataPoint.date <= t.endDate,
        ) ?? null;
      setSelected(
        trip ? { trip, latLng: e.latlng, date: clickedDataPoint.date } : null,
      );
    };

    polyline.on("mousemove", onMove);
    polyline.on("mouseout", onLeave);
    polyline.on("click", onClick);
    map.on("click", clearSelection);

    return () => {
      polyline.off("mousemove", onMove);
      polyline.off("mouseout", onLeave);
      polyline.off("click", onClick);
      map.off("click", clearSelection);
    };
  }, [dataPoints, tripsDetailsForSelectedVehicle, vehicleTripToDisplay]);

  const selectedTrip = useMemo(
    () =>
      tripsDetailsForSelectedVehicle.find(
        (trip) => trip.uuid === selected?.trip.uuid,
      ) ?? null,
    [selected, tripsDetailsForSelectedVehicle],
  );

  const firstDataPoint = dataPoints[0];
  const lastDataPoint = dataPoints[dataPoints.length - 1];

  const carBearing = useMemo(() => getCarBearing(dataPoints), [dataPoints]);

  const junctionDataPoints = useMemo(
    () => getJunctionDataPoints(dataPoints, tripsDetailsForSelectedVehicle),
    [dataPoints, tripsDetailsForSelectedVehicle],
  );

  const currentHoveredTrip = useMemo(
    () =>
      hoverTripVehicleAndDataPoint
        ? (tripsDetailsForSelectedVehicle.find(
            (t) =>
              hoverTripVehicleAndDataPoint.dataPoint.date >= t.startDate &&
              hoverTripVehicleAndDataPoint.dataPoint.date <= t.endDate,
          ) ?? null)
        : null,
    [hoverTripVehicleAndDataPoint, tripsDetailsForSelectedVehicle],
  );

  const hoveredTripToDisplay = useMemo(
    () =>
      currentHoveredTrip && currentHoveredTrip.uuid !== selectedTrip?.uuid
        ? currentHoveredTrip
        : null,
    [currentHoveredTrip, selectedTrip],
  );

  // Trips highlighted in green: the hovered one and the clicked (pinned) one.
  const highlightedTrips = useMemo(
    () =>
      [...new Set([currentHoveredTrip, selected?.trip ?? null])].filter(
        (t): t is TripsDetails => t !== null,
      ),
    [currentHoveredTrip, selected],
  );

  // Green segment for each highlighted trip — reaches the junctions via getTripDataPoints.
  const highlightedTripLines = useMemo(
    () =>
      highlightedTrips.map((trip) =>
        getTripDataPoints(trip, dataPoints, tripsDetailsForSelectedVehicle),
      ),
    [highlightedTrips, dataPoints, tripsDetailsForSelectedVehicle],
  );

  // One set of arrows per trip; highlighted trips' arrows are recolored in place (green).
  const arrowMarkers = useMemo(
    () =>
      tripsDetailsForSelectedVehicle.flatMap((trip) =>
        getArrowPositions(dataPoints, [trip]).map((a) => ({
          ...a,
          icon: createArrowIcon(
            a.bearing,
            highlightedTrips.includes(trip)
              ? TRIP_HIGHLIGHT_COLOR
              : ARROW_DEFAULT_COLOR,
          ),
        })),
      ),
    [dataPoints, tripsDetailsForSelectedVehicle, highlightedTrips],
  );

  return (
    <>
      <Polyline
        ref={polylineRef}
        positions={dataPoints.map((p) => [p.lat, p.lon])}
        color="black"
      />

      {highlightedTripLines.map(
        (points, i) =>
          points.length > 0 && (
            <Polyline
              key={`highlight-line-${i}`}
              positions={points.map((p) => [p.lat, p.lon])}
              color={TRIP_HIGHLIGHT_COLOR}
              weight={4}
              interactive={false}
            />
          ),
      )}

      {firstDataPoint && (
        <Marker
          position={[firstDataPoint.lat, firstDataPoint.lon]}
          icon={startIcon}
          interactive={false}
          zIndexOffset={1000}
        />
      )}

      {lastDataPoint && !isOngoing && (
        <Marker
          position={[lastDataPoint.lat, lastDataPoint.lon]}
          icon={endIcon}
          interactive={false}
          zIndexOffset={1000}
        />
      )}

      {lastDataPoint && isOngoing && (
        <Marker
          position={[lastDataPoint.lat, lastDataPoint.lon]}
          icon={createCarIcon(carBearing)}
          interactive={false}
          zIndexOffset={1000}
        />
      )}

      {junctionDataPoints.map((dp, i) => (
        <Marker
          key={`junction-${i}`}
          position={[dp.lat, dp.lon]}
          icon={junctionIcon}
          interactive={false}
        />
      ))}

      {arrowMarkers.map((arrow, i) => (
        <Marker
          key={`arrow-${i}`}
          position={[arrow.lat, arrow.lon]}
          icon={arrow.icon}
          interactive={false}
        />
      ))}

      {vehicleTripToDisplay && selected && selectedTrip && (
        <CircleMarker
          center={selected.latLng}
          radius={5}
          color="black"
          fill={true}
          fillColor="black"
          fillOpacity={1}
          interactive={false}
        >
          <TooltipTripCard
            vehicle={vehicleTripToDisplay}
            trip={selectedTrip}
            driver={driverByTripUuid[selectedTrip.uuid] ?? null}
            dataPointDate={selected.date}
            onRequestAddresses={() => onRequestTripAddresses(selectedTrip.uuid)}
            isPollingAddresses={
              tripUuidWaitingForAddresses === selectedTrip.uuid
            }
          />
        </CircleMarker>
      )}

      {mouseLatLng && hoverTripVehicleAndDataPoint && (
        <CircleMarker
          center={mouseLatLng}
          radius={5}
          color="black"
          fill={true}
          fillColor="black"
          fillOpacity={1}
          interactive={false}
        >
          {hoveredTripToDisplay ? (
            <TooltipTripCard
              vehicle={hoverTripVehicleAndDataPoint.vehicle}
              trip={hoveredTripToDisplay}
              driver={driverByTripUuid[hoveredTripToDisplay.uuid] ?? null}
              dataPointDate={hoverTripVehicleAndDataPoint.dataPoint.date}
            />
          ) : (
            <TooltipVehicleTrip
              vehicle={hoverTripVehicleAndDataPoint.vehicle}
              rightElement={
                <StyledHour>
                  <div>
                    {dateFormatterDayMonthYear2Digits(
                      new Date(hoverTripVehicleAndDataPoint.dataPoint.date),
                    )}
                  </div>
                  {dateFormatterHourMinute(
                    new Date(hoverTripVehicleAndDataPoint.dataPoint.date),
                  ).replaceAll(":", "h")}
                </StyledHour>
              }
              isPermanent={true}
            />
          )}
        </CircleMarker>
      )}
    </>
  );
};

const StyledHour = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const startIcon = L.icon({
  iconUrl: startTripIconUrl,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const endIcon = L.icon({
  iconUrl: endTripIconUrl,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const junctionIcon = L.icon({
  iconUrl: junctionTripIconUrl,
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

const createArrowIcon = (
  bearingDeg: number,
  color: string = ARROW_DEFAULT_COLOR,
) =>
  L.divIcon({
    className: "",
    html: `<svg width="13" height="14" viewBox="0 0 10 11" style="transform:rotate(${bearingDeg - 90}deg);transform-origin:5px 6px;display:block"><path d="M9.54477 4.11063L1.21248 10.3214L-1.93439e-05 2.82235e-05L9.54477 4.11063Z" fill="${color}"/></svg>`,
    iconSize: [13, 14],
    iconAnchor: [5, 6],
  });

const createCarIcon = (bearingDeg: number) =>
  L.divIcon({
    className: "",
    html: `<img src="${carTripIconUrl}" width="43" height="50" style="transform:rotate(${bearingDeg}deg);transform-origin:center;display:block" />`,
    iconSize: [43, 50],
    iconAnchor: [21, 25],
  });
